import { AccountTree, Build, ExitToApp, HelpOutlined, OfflinePin, Person, Star, SwitchAccount } from '@mui/icons-material';
import { Divider, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Stack, Toolbar, Tooltip, useTheme } from "@mui/material";
import { isMobile, isTablet, useMobileOrientation } from 'react-device-detect';
import logo_navy from "../../assets/images/logo_navytext_128x33.png";
import logo_white from "../../assets/images/logo_whitetext_128x33.png";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useRouteContext } from '../../hooks/use-route-context/UseRouteContext';
import { useAvailableAdminViews } from '../../queries/available-views/UseAvailableAdminViews';
import { useQueryNotifications } from '../../queries/UseQueryNotifications';
import NavigationViewList from "../navigation-view-list/NavigationViewList";
import SideSheetSwitch from '../side-sheet/side-sheet-switch/SideSheetSwitch';
import { useEvaluateFeature } from '../../queries/features/UseEvaluateFeature';
import { FeatureConstants } from '../../services/features-data-service/Models';
import { IUICustomizationSettingsDTO } from '../../services/ui-customization-data-service/Models';
import { NavDrawerAction } from './NavDrawerAction';
import { useRef } from 'react';

export type NavDrawerProps = {
    open: boolean;
    customizationSettings: IUICustomizationSettingsDTO | undefined;
    onOpen?: () => void;
    onClose?: () => void;
    onAction?: (action: NavDrawerAction) => void;
};

export default function NavDrawer({
    open,
    customizationSettings,
    onOpen,
    onClose,
    onAction,
}: NavDrawerProps) {

    const theme = useTheme();
    const isPhone = isMobile && !isTablet;
    const { isLandscape } = useMobileOrientation();
    const [offlineState] = useOfflineDetectContext();
    const routeContext = useRouteContext();

    const { data: adminViews } = useQueryNotifications(useAvailableAdminViews)({
        groupID: routeContext.groupID as number,
        enabled: !offlineState.offline
    });

    const hasAdminViews = (adminViews ?? []).length > 0;

    const { data: navigationV2Feature } = useQueryNotifications(useEvaluateFeature)({
        feature: FeatureConstants.NavigationV2,
    });

    const navigationV2 = (navigationV2Feature ?? false);

    // All this crap is for the open on hover functionality
    const openOnHover = (navigationV2 && customizationSettings?.navigationDrawer.openOnHover) ?? false;
    const openTimeout = useRef<NodeJS.Timeout | null>(null);
    const checkInterval = useRef<NodeJS.Timeout | null>(null);
    const drawerRef = useRef<HTMLDivElement | null>(null);

    // This implementation of open on hover does not use the mouseleave event to close the drawer because that event is unreliable.
    // There are ways to exit the drawer without triggering mouseleave, which leaves the drawer stuck open.
    // Instead, it uses an interval timer to check if the mouse is still hovering over the drawer and then closes it when it is not.
    function onMouseEnter() {
        if (!isPhone && !open && openOnHover && onOpen && onClose) {

            // Clear timers and start fresh every time the mouse enters the navbar
            if (openTimeout.current) {
                clearTimeout(openTimeout.current);
                openTimeout.current = null;
            }

            if (checkInterval.current) {
                clearInterval(checkInterval.current);
                checkInterval.current = null;
            }

            // Set a timeout to open the navbar after a delay
            openTimeout.current = setTimeout(() => {

                // Contunue only if the mouse is still hovering over the navbar
                if (drawerRef.current?.matches(":hover")) {

                    // If the open delay has passed, open the navbar
                    onOpen();

                    // Start checking every interval to see if the mouse is still hovering over the navbar
                    checkInterval.current = setInterval(() => {

                        // If the mouse is no longer hovering over the navbar, close it
                        if (!drawerRef.current?.matches(":hover")) {

                            // Clear interval timer
                            if (checkInterval.current) {
                                clearInterval(checkInterval.current);
                            }

                            // Close the navbar
                            onClose();
                        }
                    }, 100);
                }
            }, 500);
        }
    }

    return (
        <Drawer
            ref={drawerRef}
            variant={!isPhone ? "permanent" : "temporary"}
            onMouseEnter={onMouseEnter}
            open={open}
            onClose={onClose}
            slotProps={{
                paper: {
                    sx: {
                        position: "relative",
                        overflowX: 'hidden',
                        whiteSpace: "nowrap",
                        transition: theme.transitions.create("width", {
                            easing: theme.transitions.easing.sharp,
                            duration: open ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
                        }),
                        padding: 'env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)',
                        width: !open
                            // Rail = 56px (spec for dense layout)
                            //? theme.spacing(7)
                            ? theme.spacing(9)
                            : !isPhone
                                // Standard Drawer = 256 px
                                //? theme.spacing(32)
                                ? theme.spacing(36) //undefined
                                // Modal Drawer = Width - 56 px
                                //: `calc(100% - ${theme.spacing(7)})`,
                                : `calc(100% - ${theme.spacing(9)})`,
                    }
                }
            }}
        >
            {/* Toolbar spacer - desktop only */}
            {!isPhone &&
                <Toolbar />
            }
            <Stack sx={{ height: 1, overflow: "hidden" }}>
                <List sx={{display: 'flex', flexDirection: 'column'}} dense={false}>
                    {/* Logo - phone in portrait mode only */}
                    {isPhone && !isLandscape &&
                        <>
                            <ListItem>
                                {theme.palette.mode === "dark" &&
                                    <img src={logo_white} alt="eLynx Technologies" />
                                }
                                {theme.palette.mode === "light" &&
                                    <img src={logo_navy} alt="eLynx Technologies" />
                                }
                            </ListItem>
                            <Divider />
                        </>
                    }

                    {/* Hierarchy/Administration Item */}
                    {navigationV2
                        ? !offlineState.offline && hasAdminViews && <ListItemButton onClick={() => {
                            if (onAction) {
                                onAction(NavDrawerAction.Administration);
                            }
                        }}>
                            <ListItemIcon>
                                <Tooltip arrow placement="right" title={!open ? 'Administration' : ''}>
                                    <Build></Build>
                                </Tooltip>
                            </ListItemIcon>
                            <ListItemText primary="Administration" />
                        </ListItemButton>
                        : <ListItemButton
                            onClick={() => {
                                if (onAction) {
                                    onAction(NavDrawerAction.ToggleHierarchy);
                                }
                            }}
                        >
                            <ListItemIcon>
                                <Tooltip arrow placement="right" title={!open ? 'Hierarchy' : ''}>
                                    <AccountTree></AccountTree>
                                </Tooltip>
                            </ListItemIcon>
                            <ListItemText primary="Hierarchy" />
                        </ListItemButton>
                    }


                    {/* Favorites Item */}
                    {!offlineState.offline && <ListItem disablePadding>
                        <ListItemButton onClick={() => {
                            if (onAction) {
                                onAction(NavDrawerAction.ToggleFavorites);
                            }
                        }}
                        >
                            <ListItemIcon>
                                <Tooltip arrow placement="right" title={!open ? 'Favorites' : ''}>
                                    <Star />
                                </Tooltip>
                            </ListItemIcon>
                            <ListItemText primary="Favorites" />
                        </ListItemButton>
                    </ListItem>}

                    {/* Administration/Hierarchy Item */}
                    {!navigationV2
                        ? !offlineState.offline && hasAdminViews && <ListItemButton onClick={() => {
                            if (onAction) {
                                onAction(NavDrawerAction.Administration);
                            }
                        }}>
                            <ListItemIcon>
                                <Tooltip arrow placement="right" title={!open ? 'Administration' : ''}>
                                    <Build></Build>
                                </Tooltip>
                            </ListItemIcon>
                            <ListItemText primary="Administration" />
                        </ListItemButton>
                        : <ListItemButton
                            onClick={() => {
                                if (onAction) {
                                    onAction(NavDrawerAction.ToggleHierarchy);
                                }
                            }}
                        >
                            <ListItemIcon>
                                <Tooltip arrow placement="right" title={!open ? 'Hierarchy' : ''}>
                                    <AccountTree></AccountTree>
                                </Tooltip>
                            </ListItemIcon>
                            <ListItemText primary="Hierarchy" />
                        </ListItemButton>
                    }
                    <Divider />
                </List>

                {/* Navigation Items */}
                <NavigationViewList
                    open={open}
                    customizationSettings={customizationSettings}
                    onSelect={() => isPhone && onClose && onClose()}

                />

                {!(isPhone && isLandscape) &&
                    <List dense={false}
                        //sx={{ marginTop: "auto" }}
                        sx={{ display: 'flex', flexDirection: 'column' }}
                    >
                        <Divider />

                        {/* Account Settings Items */}
                        <ListItemButton onClick={() => {
                            if (onAction) {
                                onAction(NavDrawerAction.ShowAccount);
                            }
                        }}>
                            <ListItemIcon>
                                <Tooltip arrow placement="right" title={!open ? "Account Settings" : ""}>
                                    <Person />
                                </Tooltip>
                            </ListItemIcon>
                            <ListItemText primary="Account Settings" />
                        </ListItemButton>

                        {/* Offline mode*/}
                        {isPhone &&
                            <ListItemButton
                            //button 
                            //onClick={() => { onAction && onAction(NavDrawerAction.Offline); }} 
                            >
                                <ListItemIcon>
                                    <Tooltip arrow placement="right" title={!open ? "Offline Mode" : ""}>
                                        <OfflinePin />

                                    </Tooltip>
                                </ListItemIcon>
                                <ListItemText primary={
                                    <SideSheetSwitch
                                        label='Offline Mode'
                                        sxLabel={{
                                            marginLeft: 0
                                        }}
                                        checked={offlineState.forceOffline === true}
                                        onChange={() => {
                                            if (onAction) {
                                                onAction(NavDrawerAction.Offline);
                                            }
                                        }}
                                    />
                                } />
                            </ListItemButton>
                        }

                        {/* Change Customer Item */}
                        <ListItemButton onClick={() => {
                            if (onAction) {
                                onAction(NavDrawerAction.ChangeCustomer);
                            }
                        }}>
                            <ListItemIcon>
                                <Tooltip arrow placement="right" title={!open ? "Change Customer" : ""}>
                                    <SwitchAccount />
                                </Tooltip>
                            </ListItemIcon>
                            <ListItemText primary="Change Customer" />
                        </ListItemButton>

                        {/* Help Item */}
                        <ListItemButton onClick={() => {
                            if (onAction) {
                                onAction(NavDrawerAction.ShowHelp);
                            }
                        }}>
                            <ListItemIcon>
                                <Tooltip arrow placement="right" title={!open ? "Help" : ""}>
                                    <HelpOutlined />
                                </Tooltip>
                            </ListItemIcon>
                            <ListItemText primary="Help" />
                        </ListItemButton>

                        {/* Sign Out Item */}
                        <ListItemButton onClick={() => {
                            if (onAction) {
                                onAction(NavDrawerAction.SignOut);
                            }
                        }}>
                            <ListItemIcon>
                                <Tooltip arrow placement="right" title={!open ? "Sign Out" : ""}>
                                    <ExitToApp />
                                </Tooltip>
                            </ListItemIcon>
                            <ListItemText primary="Sign Out" />
                        </ListItemButton>
                    </List>
                }
            </Stack>
        </Drawer >
    );
}